import React from 'react';
import { Box, Typography } from '@mui/material';
import { CONCEPT_CATEGORIES, ConceptCategory, conceptTypeParam } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * The Risk Library's primary navigation.
 *
 * `concept_type` used to be one dropdown among four in the filter bar, which put a red
 * flag, a laundering typology and a tunable threshold in one undifferentiated table and
 * made the user filter before they could read anything. It is promoted here because it is
 * not a refinement of the question — it IS the question. An investigator wants red flags;
 * an MLRO wants typologies and policies; a tuning analyst wants controls. They are
 * different collections wearing one name.
 *
 * Counts come from /ontology/summary, which the parent owns. They are optional: the rail
 * must render and be usable before they arrive, and if the summary call fails the
 * categories still navigate.
 */
interface ConceptCategoryRailProps {
    /** The active `?concept_type=` value, or '' for all entries. */
    value: string;
    onChange: (conceptTypeParam: string) => void;
    /** Per-`concept_type` document counts. Absent until /summary resolves. */
    countsByType?: Record<string, number>;
    /** Total across every type, for the "All entries" row. */
    total?: number;
}

const RAIL_WIDTH = 208;

/** A category's count is the sum of the types it covers — one rail row, two stored types. */
const countFor = (
    category: ConceptCategory,
    countsByType?: Record<string, number>,
): number | null => {
    if (!countsByType) return null;
    return category.types.reduce((sum, type) => sum + (countsByType[type] || 0), 0);
};

const ConceptCategoryRail: React.FC<ConceptCategoryRailProps> = ({
    value, onChange, countsByType, total,
}) => {
    const rows: { key: string; label: string; count: number | null; param: string }[] = [
        {
            key: 'all',
            label: 'All entries',
            count: countsByType ? (total ?? null) : null,
            param: '',
        },
        ...CONCEPT_CATEGORIES.map((category) => ({
            key: category.id,
            label: category.label,
            count: countFor(category, countsByType),
            param: conceptTypeParam(category),
        })),
    ];

    return (
        <Box
            component="nav"
            aria-label="Risk library categories"
            sx={{ width: RAIL_WIDTH, flexShrink: 0, display: { xs: 'none', md: 'block' } }}
        >
            {rows.map((row) => {
                const isActive = row.param === value;
                return (
                    <Box
                        key={row.key}
                        component="button"
                        type="button"
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => onChange(row.param)}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            mb: 0.25,
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: '"Inter", sans-serif',
                            backgroundColor: isActive ? dsVar('raised') : 'transparent',
                            boxShadow: isActive ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                            '&:hover': { backgroundColor: dsVar('raised') },
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '0.8125rem',
                                fontWeight: isActive ? 600 : 500,
                                color: isActive ? dsVar('fg') : dsVar('fg-2'),
                            }}
                        >
                            {row.label}
                        </Typography>

                        {/* A count of zero is worth showing — "no typologies yet" is an
                            answer. Only an UNKNOWN count renders as nothing. */}
                        {row.count !== null && (
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '0.6875rem',
                                    color: isActive ? dsVar('accent-fg') : dsVar('fg-muted'),
                                }}
                            >
                                {row.count}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default ConceptCategoryRail;
